import { useCreateJobMutation } from '@/hooks/useCreateJobMutation';
import { useReplyAll } from '@/hooks/useReplyAll';
import { useStoreOpenOutlookEmail } from '@/hooks/useStoreOpenOutlookEmail';
import {
  EmailThreadContext,
  SharedCreateQuoteJobArgs,
} from '@shared/contexts/pluginContexts/EmailThreadContext';
import { parseNotifications } from '@shared/features/automatic-navigator/AutomaticNavigator';
import { JobType } from '@shared/generated/graphql';
import { JobEmailMetadata } from '@shared/types/job';
import { isNil } from 'lodash';
import { useOutlookAdaptor } from './useOutlookAdaptor';
import { getItemSharedMailbox } from './utils/getItemSharedMailbox';
import { getRestfulId } from './utils/getRestfulId';
import { getViewedMailboxAddress } from './utils/getViewedMailboxAddress';

function hasMessageItem(item?: Office.MessageRead): item is Office.MessageRead {
  if (isNil(item)) return false;
  return item.itemType === Office.MailboxEnums.ItemType.Message;
}

function getEmailMetadata(
  item: Office.MessageRead,
  sharedMailboxAddress?: string
): JobEmailMetadata {
  const { emailAddress } = Office.context.mailbox.userProfile;
  return {
    sender: emailAddress,
    replyTo: getViewedMailboxAddress(sharedMailboxAddress),
    subject: item.subject,
    messageId: item.internetMessageId,
    itemId: getRestfulId(item),
    originatorAddress: item.sender.emailAddress,
  };
}

type OutlookThreadContextProviderProps = {
  children: JSX.Element;
};
export const OutlookThreadContextProvider = ({
  children,
}: OutlookThreadContextProviderProps) => {
  const createOrderJob = useCreateJobMutation();
  const createQuoteJob = useCreateJobMutation();
  const reply = useReplyAll();
  const storeOutlookEmail = useStoreOpenOutlookEmail();
  const {
    item,
    itemBody,
    isSharedInbox,
    sharedMailboxAddress,
    sharedInboxCandidates,
    selectSharedInboxCandidate,
    jobs,
    jobsLoading,
    jobTypes,
    storeJobType,
    hasUnconfirmedQuote,
    hasUnconfirmedOrder,
    isComposeMode,
  } = useOutlookAdaptor();

  const createOrderFromThread = async () => {
    if (hasMessageItem(item)) {
      const res = await createOrderJob.createJob({
        restfulItemId: getRestfulId(item),
        isSharedInbox,
        emailMetadata: getEmailMetadata(item, sharedMailboxAddress),
        jobType: JobType.ORDERS,
      });
      return res.data?.createOutlookJob.job;
    }
  };

  const createQuoteFromThread = async ({
    buildHint,
  }: SharedCreateQuoteJobArgs) => {
    if (hasMessageItem(item)) {
      const res = await createQuoteJob.createJob({
        restfulItemId: getRestfulId(item),
        isSharedInbox,
        emailMetadata: getEmailMetadata(item, sharedMailboxAddress),
        jobType: JobType.QUOTES,
        buildHint,
      });
      return res.data?.createOutlookJob.job;
    }
  };

  const thisItem = item && {
    id: item.itemId,
    sender: {
      emailAddress: item.sender.emailAddress,
    },
  };

  const sharedInbox = {
    isSharedInbox,
    address: sharedMailboxAddress,
    candidates: sharedInboxCandidates,
    selectCandidate: selectSharedInboxCandidate,
  };

  const threadJobs = {
    jobs,
    jobTypes,
    storeJobType,
    loading: jobsLoading,
  };

  const createOrder = {
    call: createOrderFromThread,
    error: createOrderJob.error,
    loading: createOrderJob.loading,
  };

  const createQuote = {
    call: createQuoteFromThread,
    error: createQuoteJob.error,
    loading: createQuoteJob.loading,
  };

  const storeOpenEmail = async () => {
    if (!hasMessageItem(item)) return undefined;
    return storeOutlookEmail(item, await getItemSharedMailbox(item));
  };

  const openThread = (emailMetadata: JobEmailMetadata) => {
    if (emailMetadata.itemId) {
      Office.context.mailbox.displayMessageForm(emailMetadata.itemId);
    }
  };

  return (
    <EmailThreadContext.Provider
      value={{
        thisItem,
        notifications: parseNotifications(itemBody),
        threadJobs,
        createOrder,
        createQuote,
        sharedInbox,
        reply,
        openThread,
        storeOpenEmail,
        hasUnconfirmedQuote,
        hasUnconfirmedOrder,
        hideInsertDraftButton: isComposeMode,
        isComposeMode,
      }}
    >
      {children}
    </EmailThreadContext.Provider>
  );
};
